// eslint-disable-next-line no-restricted-imports
import {
  atom,
  Provider,
  useAtom,
  useAtomValue,
  useSetAtom,
  createStore,
  type PrimitiveAtom,
} from "jotai";
import { useLayoutEffect } from "react";

import type { Profile, Whiteboard, WhiteboardFolder } from "./types/profile";

export const appJotaiStore = createStore();

// Profile & Whiteboard atoms
export const currentProfileAtom = atom<Profile | null>(null);
export const whiteboardsAtom = atom<Whiteboard[]>([]);
export const currentWhiteboardAtom = atom<Whiteboard | null>(null);
export const whiteboardsLoadingAtom = atom<boolean>(false);
export const whiteboardSwitchingAtom = atom<boolean>(false);
export const whiteboardFoldersAtom = atom<WhiteboardFolder[]>([]);

// Content cache for loaded whiteboards (session storage)
export interface WhiteboardContent {
  elements: any[];
  files: Record<string, any>;
  loadedAt: number;
}

export const whiteboardContentCacheAtom = atom<Map<string, WhiteboardContent>>(
  new Map(),
);

// Save state tracking
export type SaveState = "saved" | "saving" | "unsaved";
export const saveStateAtom = atom<SaveState>("saved");

// Last saved state for comparison (to detect actual changes)
export const lastSavedStateAtom = atom<{
  elementsLength: number;
  filesCount: number;
  versionSum: number;
} | null>(null);

// Save cancellation function
export const cancelSaveAtom = atom<(() => void) | null>(null);

// Local backup settings
export const localBackupEnabledAtom = atom<boolean>(false);
export const localBackupFolderHandleAtom =
  atom<FileSystemDirectoryHandle | null>(null);

export { atom, Provider, useAtom, useAtomValue, useSetAtom };

export const useAtomWithInitialValue = <
  T extends unknown,
  A extends PrimitiveAtom<T>,
>(
  atom: A,
  initialValue: T | (() => T),
) => {
  const [value, setValue] = useAtom(atom);

  useLayoutEffect(() => {
    if (typeof initialValue === "function") {
      // @ts-ignore
      setValue(initialValue());
    } else {
      setValue(initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [value, setValue] as const;
};
